/**
 * MonthView component for calendar
 * Displays events in a monthly grid layout
 */

'use client';

import type { Event } from '../types';
import { useEventsComponents } from '../config';
import { darkenHexColorWithOpacity as defaultDarkenHexColor } from '../utils/color';
import { useMemo, useState, Fragment } from 'react';
import { useEventDrawer } from '../cards/event-card';

interface MonthViewProps {
  events: Event[];
  currentDate: Date;
  baseUrl?: string;
  /** Days of the week to hide (0 = Sunday, 1 = Monday, ..., 6 = Saturday) */
  hiddenDays?: number[];
  /** Maximum number of events to display per day before showing "+N more" */
  maxEventsPerDay?: number;
}

interface DayCell {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: Event[];
}

/**
 * Extract event type color from event
 */
function getEventTypeColor(event: Event): string | undefined {
  const eventTypeItem = Array.isArray(event.eventType) ? event.eventType[0] : event.eventType;
  return eventTypeItem && 'eventTypeColor' in eventTypeItem
    ? (eventTypeItem.eventTypeColor ?? undefined)
    : undefined;
}

/**
 * DayEventsList - Reusable component for rendering events in a day cell
 */
function DayEventsList({
  day,
  maxEventsPerDay,
  expandedDays,
  toggleDayExpansion,
  darkenHexColorWithOpacity,
  LinkComponent,
  variant = 'mobile',
}: {
  day: DayCell;
  maxEventsPerDay: number;
  expandedDays: Set<string>;
  toggleDayExpansion: (key: string) => void;
  darkenHexColorWithOpacity: (color: string, darken: number, opacity: number) => string;
  LinkComponent: any;
  variant?: 'mobile' | 'desktop';
}) {
  const drawerContext = useEventDrawer();

  const dayKey = `${day.date.getFullYear()}-${day.date.getMonth()}-${day.date.getDate()}`;
  const isExpanded = expandedDays.has(dayKey);
  const eventsToShow = isExpanded ? day.events : day.events.slice(0, maxEventsPerDay);
  const hiddenCount = day.events.length > maxEventsPerDay ? day.events.length - maxEventsPerDay : 0;

  // Desktop has stronger colors and white text
  const opacity = variant === 'desktop' ? 0.8 : 0.15;
  const textColorClass = variant === 'desktop' ? 'text-white' : '';
  const locationColorClass =
    variant === 'desktop'
      ? 'text-white font-light tracking-tight opacity-80'
      : 'text-muted-foreground';

  return (
    <>
      {eventsToShow.map(event => {
        const eventLink = event.path;
        if (!eventLink) return null;

        const eventTypeColor = getEventTypeColor(event);
        const className = `group block text-left w-full rounded-xs p-1.5 text-xs transition-colors hover:scale-105 cursor-pointer ${textColorClass} ${
          eventTypeColor ? '' : 'bg-primary/10 hover:bg-primary/20'
        }`;
        
        // IMPORTANT: Only apply style if eventTypeColor exists
        const style = eventTypeColor
          ? {
              backgroundColor: darkenHexColorWithOpacity(eventTypeColor, 60, opacity),
            }
          : {};

        const content = (
          <div>
            <span className="font-medium leading-tight mr-1">{event.title}</span>
            {event.eventLocation && (
              <span
                className={`${locationColorClass} ${variant === 'mobile' ? 'block md:inline-block' : ''} text-xs`}
              >
                {event.eventLocation}
              </span>
            )}
          </div>
        );

        // If drawer context available, use button with onClick
        if (drawerContext) {
          return (
            <button
              key={event.id}
              onClick={() => drawerContext.openDrawer(event)}
              className={className}
              style={style}
            >
              {content}
            </button>
          );
        }

        // Otherwise use Link for navigation
        return (
          <LinkComponent key={event.id} href={eventLink} className={className} style={style}>
            {content}
          </LinkComponent>
        );
      })}
      {hiddenCount > 0 && (
        <button
          onClick={() => toggleDayExpansion(dayKey)}
          className="w-full text-center text-[10px] font-medium text-primary hover:text-primary/80 transition-colours"
          aria-label={isExpanded ? `Hide ${hiddenCount} events` : `Show ${hiddenCount} more events`}
        >
          {isExpanded ? `Show less` : `+${hiddenCount} more`}
        </button>
      )}
    </>
  );
}

/**
 * DayCell - Reusable component for rendering a day cell with keyboard navigation
 */
function DayCellContent({
  day,
  dayIndex,
  totalDays,
  maxEventsPerDay,
  expandedDays,
  toggleDayExpansion,
  darkenHexColorWithOpacity,
  LinkComponent,
  variant = 'mobile',
}: {
  day: DayCell;
  dayIndex: number;
  totalDays: number;
  maxEventsPerDay: number;
  expandedDays: Set<string>;
  toggleDayExpansion: (key: string) => void;
  darkenHexColorWithOpacity: (color: string, darken: number, opacity: number) => string;
  LinkComponent: any;
  variant?: 'mobile' | 'desktop';
}) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Keyboard navigation for calendar
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (day.events.length > 0) {
          toggleDayExpansion(
            `${day.date.getFullYear()}-${day.date.getMonth()}-${day.date.getDate()}`
          );
        }
        break;
    }
  };

  const formattedDate = day.date.toLocaleDateString('en-GB', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div
      className={`min-h-[100px] border-b border-slate-500/5 p-2 sm:min-h-[120px] ${
        dayIndex < totalDays - 1 ? 'border-r border-slate-500/5' : ''
      } ${day.isCurrentMonth ? 'bg-background' : 'bg-muted/30'} ${
        day.isToday ? 'ring-2 ring-primary ring-inset' : ''
      }`}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      role="button"
      aria-label={`${formattedDate}, ${day.events.length} ${day.events.length === 1 ? 'event' : 'events'}`}
    >
      {/* Date number */}
      <div className="mb-1 flex items-center justify-between">
        <span
          className={`flex h-6 w-6 items-center justify-center rounded-full text-sm ${
            day.isToday
              ? 'bg-primary font-bold text-primary-foreground'
              : day.isCurrentMonth
                ? 'font-medium'
                : 'text-muted-foreground'
          }`}
        >
          {day.date.getDate()}
        </span>
        {day.events.length > 0 && (
          <span className="rounded-full bg-primary/20 px-1.5 py-0.5 text-xs font-medium text-primary">
            {day.events.length}
          </span>
        )}
      </div>

      {/* Events list */}
      <div className="space-y-1">
        <DayEventsList
          day={day}
          maxEventsPerDay={maxEventsPerDay}
          expandedDays={expandedDays}
          toggleDayExpansion={toggleDayExpansion}
          darkenHexColorWithOpacity={darkenHexColorWithOpacity}
          LinkComponent={LinkComponent}
          variant={variant}
        />
      </div>
    </div>
  );
}

export function MonthView({
  events,
  currentDate,
  baseUrl = '/events',
  hiddenDays = [],
  maxEventsPerDay = 6,
}: MonthViewProps) {
  const config = useEventsComponents();
  const Link = config.LinkComponent;
  const darkenHexColorWithOpacity = config.darkenHexColorWithOpacity || defaultDarkenHexColor;

  // Track which days have expanded event lists
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());

  const toggleDayExpansion = (dayKey: string) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(dayKey)) {
        next.delete(dayKey);
      } else {
        next.add(dayKey);
      }
      return next;
    });
  };

  /**
   * Get ISO week number for a given date
   * ISO 8601 week numbering: Week 1 is the week with the first Thursday of the year
   */
  const getISOWeekNumber = (date: Date): number => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  };

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Calculate start and end of calendar grid (including prev/next month days)
    const startDay = new Date(firstDay);
    startDay.setDate(startDay.getDate() - startDay.getDay()); // Start on Sunday

    const endDay = new Date(lastDay);
    endDay.setDate(endDay.getDate() + (6 - endDay.getDay())); // End on Saturday

    // Build array of days
    const days: DayCell[] = [];
    const currentDay = new Date(startDay);

    while (currentDay <= endDay) {
      const dayDate = new Date(currentDay);
      dayDate.setHours(0, 0, 0, 0);
      const dayOfWeek = dayDate.getDay();

      // Skip hidden days
      if (hiddenDays.includes(dayOfWeek)) {
        currentDay.setDate(currentDay.getDate() + 1);
        continue;
      }

      // Find events for this day
      const dayEvents = events.filter(event => {
        const startTimestamp = event.eventDateTimeRange?.start?.timestamp;
        const endTimestamp = event.eventDateTimeRange?.end?.timestamp;
        if (!startTimestamp || !endTimestamp) return false;

        const eventStart = new Date(startTimestamp * 1000);
        eventStart.setHours(0, 0, 0, 0);

        const eventEnd = new Date(endTimestamp * 1000);
        eventEnd.setHours(23, 59, 59, 999);

        return dayDate >= eventStart && dayDate <= eventEnd;
      });

      days.push({
        date: new Date(dayDate),
        isCurrentMonth: dayDate.getMonth() === month,
        isToday: dayDate.getTime() === today.getTime(),
        events: dayEvents,
      });

      currentDay.setDate(currentDay.getDate() + 1);
    }

    return days;
  }, [currentDate, events, hiddenDays]);

  // Group days by week and calculate week numbers
  const weeksData = useMemo(() => {
    const weeks: Array<{ weekNumber: number; days: DayCell[] }> = [];
    let currentWeek: DayCell[] = [];
    let currentWeekNumber: number | null = null;

    calendarDays.forEach((day, index) => {
      const weekNumber = getISOWeekNumber(day.date);

      // Start new week if week number changes or it's the first day
      if (currentWeekNumber === null || currentWeekNumber !== weekNumber) {
        // Save previous week if it exists
        if (currentWeek.length > 0 && currentWeekNumber !== null) {
          weeks.push({ weekNumber: currentWeekNumber, days: currentWeek });
        }
        // Start new week
        currentWeek = [day];
        currentWeekNumber = weekNumber;
      } else {
        currentWeek.push(day);
      }

      // Save last week
      if (
        index === calendarDays.length - 1 &&
        currentWeek.length > 0 &&
        currentWeekNumber !== null
      ) {
        weeks.push({ weekNumber: currentWeekNumber, days: currentWeek });
      }
    });

    return weeks;
  }, [calendarDays]);

  // Day headers - filter out hidden days
  const allDayHeaders = [
    { label: 'Sun', short: 'S', dayOfWeek: 0 },
    { label: 'Mon', short: 'M', dayOfWeek: 1 },
    { label: 'Tue', short: 'T', dayOfWeek: 2 },
    { label: 'Wed', short: 'W', dayOfWeek: 3 },
    { label: 'Thu', short: 'T', dayOfWeek: 4 },
    { label: 'Fri', short: 'F', dayOfWeek: 5 },
    { label: 'Sat', short: 'S', dayOfWeek: 6 },
  ];

  const visibleDayHeaders = allDayHeaders.filter(header => !hiddenDays.includes(header.dayOfWeek));
  const visibleColumnsCount = 7 - hiddenDays.length;

  // Common props for DayCellContent
  const dayCellProps = {
    maxEventsPerDay,
    expandedDays,
    toggleDayExpansion,
    darkenHexColorWithOpacity,
    LinkComponent: Link,
  };

  return (
    <div className="overflow-hidden">
      {/* Day headers - mobile version without week column */}
      <div
        className="grid border-b border-slate-500/5 bg-muted/50 md:hidden"
        style={{
          gridTemplateColumns: `repeat(${visibleColumnsCount}, minmax(0, 1fr))`,
        }}
      >
        {visibleDayHeaders.map((header, index) => (
          <div
            key={header.dayOfWeek}
            className={`p-2 text-center text-xs font-medium text-muted-foreground ${
              index < visibleDayHeaders.length - 1 ? 'border-r border-slate-500/5' : ''
            }`}
          >
            <span className="hidden sm:inline">{header.label}</span>
            <span className="sm:hidden">{header.short}</span>
          </div>
        ))}
      </div>

      {/* Day headers - desktop version with week column */}
      <div
        className="hidden md:grid border-b border-slate-500/5 bg-muted/50"
        style={{ gridTemplateColumns: `45px repeat(${visibleColumnsCount}, minmax(0, 1fr))` }}
      >
        <div className="border-r border-slate-500/5 p-2 text-center text-xs font-medium text-muted-foreground">
          Week
        </div>
        {visibleDayHeaders.map((header, index) => (
          <div
            key={`desktop-header-${header.dayOfWeek}`}
            className={`p-2 text-center text-xs font-medium text-muted-foreground ${
              index < visibleDayHeaders.length - 1 ? 'border-r border-slate-500/5' : ''
            }`}
          >
            <span className="hidden sm:inline">{header.label}</span>
            <span className="sm:hidden">{header.short}</span>
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="space-y-0">
        {weeksData.map((week, weekIndex) => (
          <Fragment key={weekIndex}>
            {/* Mobile version - without week column */}
            <div
              key={`mobile-${weekIndex}`}
              className="grid border-b border-slate-500/5 md:hidden"
              style={{ gridTemplateColumns: `repeat(${visibleColumnsCount}, minmax(0, 1fr))` }}
            >
              {week.days.map((day, dayIndex) => (
                <DayCellContent
                  key={`${week.weekNumber}-${dayIndex}-mobile`}
                  day={day}
                  dayIndex={dayIndex}
                  totalDays={week.days.length}
                  {...dayCellProps}
                  variant="mobile"
                />
              ))}
            </div>

            {/* Desktop version - with week column */}
            <div
              key={`desktop-${weekIndex}`}
              className="hidden md:grid border-b border-slate-500/5"
              style={{ gridTemplateColumns: `45px repeat(${visibleColumnsCount}, minmax(0, 1fr))` }}
            >
              {/* Week number cell */}
              <div className="border-r border-slate-500/5 bg-muted/30 px-1 py-1 pt-2 text-center text-xs font-medium text-muted-foreground">
                W{week.weekNumber.toString().padStart(2, '0')}
              </div>
              {/* Days in week - desktop version */}
              {week.days.map((day, dayIndex) => (
                <DayCellContent
                  key={`${week.weekNumber}-${dayIndex}-desktop`}
                  day={day}
                  dayIndex={dayIndex}
                  totalDays={week.days.length}
                  {...dayCellProps}
                  variant="desktop"
                />
              ))}
            </div>
          </Fragment>
        ))}
      </div>
    </div>
  );
}
